// GID-116: multi-line composer layout.
//
// The composer is one flex row: [ textarea | model tier | attach | send ].
// While the message fits on a single line that reads fine, but as soon as it
// wraps, the textarea (flex: 1, min-width: 72px) keeps shrinking to make room
// for the controls, so a long message is typed into a narrow column with the
// buttons eating the horizontal space beside it.
//
// Once the text wraps we give the textarea the whole first row and let the
// controls wrap onto a row of their own underneath — the ChatGPT composer
// behaviour. .input-wrapper is already `flex-wrap: wrap` + `justify-content:
// flex-end`, so the controls right-align on that second row for free; the only
// rule needed is a full-width basis on the textarea (.is-multiline in
// taskpane.css).
//
// THE TRAP: the obvious implementation — "does the textarea currently render
// more than one line?" — oscillates on every keystroke. Inline, the textarea is
// narrow, so the text wraps to 2 lines; that flips the class on; at full width
// the same text fits on 1 line; that flips the class back off; repeat. The
// measurement has to be INVARIANT to the class it controls, so we always ask
// the same question regardless of the current layout:
//
//     "how many lines would this text need at the INLINE width?"
//
// The inline width is derived from the controls' widths (which don't change
// with the class), not from the textarea's current width. Measurement happens
// in an offscreen mirror so we never have to thrash the real layout.

/**
 * Does this content occupy more than one line?
 *
 * Kept free of DOM lookups so the threshold logic is unit-testable: callers
 * pass the already-measured box metrics.
 *
 * @param {Object} m
 * @param {number} m.scrollHeight - content height incl. vertical padding
 * @param {number} m.lineHeight   - computed line-height, px
 * @param {number} [m.paddingTop]
 * @param {number} [m.paddingBottom]
 * @returns {boolean}
 */
export function isMultiline({ scrollHeight, lineHeight, paddingTop = 0, paddingBottom = 0 }) {
  const lh = Number(lineHeight);
  // A non-finite/zero line-height would make the division meaningless — treat
  // it as single-line rather than flipping the layout on bad measurements.
  if (!Number.isFinite(lh) || lh <= 0) return false;

  const contentHeight = Number(scrollHeight) - paddingTop - paddingBottom;
  if (!Number.isFinite(contentHeight) || contentHeight <= 0) return false;

  // Round before comparing: sub-pixel line-heights (e.g. 1.4 * 14 = 19.6px)
  // otherwise make a single line measure as 1.02 lines and latch multi-line on.
  return Math.round(contentHeight / lh) > 1;
}

const MIRROR_CLASS = "composer-measure-mirror";

/**
 * Width the textarea gets in the INLINE layout = the wrapper's content box
 * minus every sibling control and the gaps between them.
 *
 * Deliberately derived from the CONTROLS, not from the textarea: the controls
 * keep their size in both layouts, so this returns the same value whether or
 * not .is-multiline is currently set. That invariance is what stops the
 * oscillation.
 *
 * @returns {number} px, or NaN if it can't be determined
 */
export function inlineTextareaWidth(wrapper, textarea, getStyle) {
  if (!wrapper || !textarea) return NaN;
  const cs = getStyle(wrapper);
  if (!cs) return NaN;

  const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  const gap = parseFloat(cs.gap) || parseFloat(cs.columnGap) || 0;

  let controlsWidth = 0;
  let visibleControls = 0;
  for (const child of Array.from(wrapper.children || [])) {
    if (child === textarea) continue;
    // The measuring mirror lives inside the wrapper but is absolutely
    // positioned, so it occupies no row space. Counting it would subtract its
    // own width from the available width on every call — driving the result to
    // zero and disabling the layout entirely.
    if (child.classList && child.classList.contains(MIRROR_CLASS)) continue;
    const w = child.getBoundingClientRect ? child.getBoundingClientRect().width : 0;
    if (w > 0) {
      controlsWidth += w;
      visibleControls += 1;
    }
  }

  // One gap per control sitting beside the textarea.
  return wrapper.clientWidth - padX - controlsWidth - gap * visibleControls;
}

// Offscreen twin of the textarea, used to measure wrapping at an arbitrary
// width without disturbing the real one.
function getMirror(wrapper, doc) {
  let mirror = wrapper.querySelector(":scope > ." + MIRROR_CLASS);
  if (!mirror) {
    mirror = doc.createElement("div");
    mirror.className = MIRROR_CLASS;
    mirror.setAttribute("aria-hidden", "true");
    wrapper.appendChild(mirror);
  }
  return mirror;
}

/**
 * Auto-grow the textarea and set the multi-line layout flag on its wrapper.
 * Safe to call on any state (including an empty textarea after send).
 *
 * @param {HTMLTextAreaElement} el
 * @param {Object} [opts]
 * @param {number} [opts.maxHeight=120] - matches .message-input max-height
 * @returns {boolean} whether the stacked layout is active
 */
export function applyComposerLayout(el, { maxHeight = 120 } = {}) {
  if (!el || typeof window === "undefined") return false;

  const wrapper = el.closest ? el.closest(".input-wrapper") : null;
  const getStyle = window.getComputedStyle ? (n) => window.getComputedStyle(n) : () => null;
  const cs = getStyle(el);

  let multiline = false;
  if (wrapper && cs) {
    const width = inlineTextareaWidth(wrapper, el, getStyle);
    if (Number.isFinite(width) && width > 0) {
      const mirror = getMirror(wrapper, el.ownerDocument || window.document);
      // Match the textarea's text layout exactly, then size to the INLINE width.
      Object.assign(mirror.style, {
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        top: "0",
        left: "-9999px",
        whiteSpace: "pre-wrap",
        overflowWrap: "break-word",
        wordBreak: cs.wordBreak,
        font: cs.font,
        fontSize: cs.fontSize,
        fontFamily: cs.fontFamily,
        fontWeight: cs.fontWeight,
        lineHeight: cs.lineHeight,
        letterSpacing: cs.letterSpacing,
        padding: "0",
        border: "0",
        width: width + "px",
      });
      // Trailing newline needs a character or the browser collapses the line.
      mirror.textContent = (el.value || "") + "​";

      multiline = isMultiline({
        scrollHeight: mirror.scrollHeight,
        lineHeight: parseFloat(cs.lineHeight),
      });
    }
    wrapper.classList.toggle("is-multiline", multiline);
  }

  // Height AFTER the class is applied, so scrollHeight reflects the final
  // width. Reset first or the box can never shrink back down.
  el.style.height = "auto";
  el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";

  return multiline;
}
